/**
 * The phone Rota tab (docs/mobile-design.md §4.10, t182u), shared by mobile web and React Native: the week strip and
 * its per-day coverage and dots, the day view grouped by position or building, the department / position filters,
 * what counts as an unpublished change, who's in grouped by status, and the shift sheet's rules (candidates, copy /
 * repeat targets, publish choices). Pure TS.
 */
import { DateTime } from 'luxon';
import type { BulkShiftOp, Shift, ShiftCandidate, WtrWarning } from '../workforce/types';
import type { AttendanceRow, LiveStatus } from '../workforce/attendance';
import { axisPositions, type PositionBuildingsLike } from '../workforce/positionBuildings';

const d = (iso: string) => DateTime.fromISO(iso, { zone: 'Europe/London' });
export const ROTA_STORE_KEY = 'collab.rota.mobile';

// ------------------------------------------------------------------------------------------------ dates
/** Monday of the week holding `date` (en-GB weeks start on Monday). */
export const rotaWeekStart = (date: string): string => d(date).startOf('week').toISODate()!;
/** The 7 dates of the week holding `date`, Monday first. */
export function rotaWeek(date: string): string[] {
  const mon = d(rotaWeekStart(date));
  return Array.from({ length: 7 }, (_, i) => mon.plus({ days: i }).toISODate()!);
}
export const rotaAddDays = (date: string, n: number): string => d(date).plus({ days: n }).toISODate()!;
/** "Tue 20 Oct" and the strip's two lines ("Tue", "20"). */
export const rotaDayLabel = (date: string): string => d(date).setLocale('en-GB').toFormat('ccc d LLL');
export const rotaStripLabel = (date: string): { dow: string; day: string } => ({ dow: d(date).setLocale('en-GB').toFormat('ccc'), day: d(date).toFormat('d') });
/** "12–18 Oct" or "27 Oct – 2 Nov". */
export function rotaWeekLabel(date: string): string {
  const days = rotaWeek(date); const a = d(days[0]!); const b = d(days[6]!);
  return a.month === b.month ? `${a.toFormat('d')}–${b.setLocale('en-GB').toFormat('d LLL')}` : `${a.setLocale('en-GB').toFormat('d LLL')} – ${b.setLocale('en-GB').toFormat('d LLL')}`;
}

// ------------------------------------------------------------------------------------------------ shifts
export type RotaShift = Pick<Shift, 'id' | 'local_date' | 'starts_at' | 'ends_at' | 'user_id' | 'position_id' | 'location_id' | 'open_slots' | 'status' | 'has_unpublished_changes'> & Partial<Pick<Shift, 'pending_claims'>>;
const live = <S extends RotaShift>(s: S) => s.status !== 'cancelled';
/** A shift that staff can't see yet, or whose change they can't see yet: what Publish sends. */
export const isUnpublished = (s: Pick<Shift, 'status' | 'has_unpublished_changes'>) => s.status === 'draft' || (s.status === 'published' && s.has_unpublished_changes);
/** Places on a shift: a person's shift is 1; an open shift is its open slots. */
export const shiftPlaces = (s: Pick<Shift, 'user_id' | 'open_slots'>) => (s.user_id ? 1 : Math.max(1, s.open_slots || 1));

export interface RotaDayCoverage { date: string; assigned: number; places: number; open: number; unpublished: number; warnings: number }
/** Per day: people on, places in all, open places left, unpublished changes and shifts with warnings. */
export function rotaDayCoverage(shifts: readonly RotaShift[], warnings: readonly Pick<WtrWarning, 'shift_id' | 'severity'>[], date: string): RotaDayCoverage {
  const day = shifts.filter(s => live(s) && s.local_date === date);
  const ids = new Set(day.map(s => s.id));
  const warned = new Set(warnings.filter(w => w.shift_id && ids.has(w.shift_id) && w.severity !== 'info').map(w => w.shift_id!));
  const assigned = day.filter(s => s.user_id).length;
  const open = day.filter(s => !s.user_id).reduce((n, s) => n + shiftPlaces(s), 0);
  return { date, assigned, places: assigned + open, open, unpublished: day.filter(isUnpublished).length, warnings: warned.size };
}
export const weekCoverage = (shifts: readonly RotaShift[], warnings: readonly Pick<WtrWarning, 'shift_id' | 'severity'>[], date: string) =>
  rotaWeek(date).map(x => rotaDayCoverage(shifts, warnings, x));
/** The orange dot: warnings, or open places nobody has taken yet. */
export const dayNeedsAttention = (c: RotaDayCoverage) => c.warnings > 0 || c.open > 0;

/**
 * Position and department filters (t199u). An empty list means everything in that section is ticked (the default);
 * otherwise the ticked ids. `none*` marks a section with every box cleared: it then matches nothing, rather than all.
 */
export interface RotaFilters {
  positions: string[]; teams: string[]; nonePositions?: boolean; noneTeams?: boolean;
  /** Groups (team kind 'group'), filtered like departments and combined with them (AND). */
  groups?: string[]; noneGroups?: boolean;
}
export const EMPTY_ROTA_FILTERS: RotaFilters = { positions: [], teams: [], groups: [] };
export type RotaFilterSection = 'positions' | 'teams' | 'groups';
const NONE: Record<RotaFilterSection, 'nonePositions' | 'noneTeams' | 'noneGroups'> = { positions: 'nonePositions', teams: 'noneTeams', groups: 'noneGroups' };
const picked = (f: RotaFilters, k: RotaFilterSection): string[] => f[k] ?? [];
const narrowed = (f: RotaFilters, k: RotaFilterSection) => (picked(f, k).length || f[NONE[k]] ? 1 : 0);
/** How many sections are narrowed (for the dot / count on the Filter button). */
export const rotaFilterCount = (f: RotaFilters) => narrowed(f, 'positions') + narrowed(f, 'teams') + narrowed(f, 'groups');
/** Is this id ticked in the sheet? */
export const rotaFilterOn = (f: RotaFilters, k: RotaFilterSection, id: string) => !f[NONE[k]] && (!picked(f, k).length || picked(f, k).includes(id));
/** Tick or untick one id; `all` is every id in the section (so "all ticked" folds back to the default). */
export function rotaFilterToggle(f: RotaFilters, k: RotaFilterSection, id: string, all: readonly string[]): RotaFilters {
  const ticked = f[NONE[k]] ? [] : picked(f, k).length ? picked(f, k) : [...all];
  const next = ticked.includes(id) ? ticked.filter(x => x !== id) : [...ticked, id];
  if (!next.length) return { ...f, [k]: [], [NONE[k]]: true };
  return { ...f, [k]: all.every(x => next.includes(x)) ? [] : next, [NONE[k]]: false };
}
/**
 * t219u: the sheet offers only the positions that apply (the building's, for the departments ticked). Ticked positions
 * that no longer apply are dropped; "all" stays "all", and a subset that loses every id goes back to "all" rather
 * than silently hiding everything.
 */
export function rotaFilterPrune(f: RotaFilters, k: RotaFilterSection, valid: readonly string[]): RotaFilters {
  if (f[NONE[k]] || !picked(f, k).length) return f;
  const kept = picked(f, k).filter(id => valid.includes(id));
  return { ...f, [k]: kept.length === valid.length ? [] : kept };
}
/** Saved filters from older versions (or junk) become the default: every box ticked. */
export function migrateRotaFilters(v: unknown): RotaFilters {
  const o = (v && typeof v === 'object' ? v : {}) as Partial<RotaFilters>;
  const ids = (x: unknown) => (Array.isArray(x) ? x.filter((y): y is string => typeof y === 'string') : []);
  return { positions: ids(o.positions), teams: ids(o.teams), groups: ids(o.groups), nonePositions: o.nonePositions === true, noneTeams: o.noneTeams === true, noneGroups: o.noneGroups === true };
}
/** Positions the filter sheet offers: those on this week's shifts for the ticked departments (open shifts too); all when there are none. */
export function rotaFilterPositions<P extends PositionBuildingsLike>(positions: readonly P[], shifts: readonly RotaShift[], f: RotaFilters, teamMembers: ReadonlyMap<string, ReadonlySet<string>>, locationId?: string | null): P[] {
  const used = new Set(filterRota(shifts, { ...f, positions: [], nonePositions: false }, teamMembers).map(s => s.position_id).filter(Boolean) as string[]);
  const list = positions.filter(p => used.has(p.id));
  // t477u: with nothing on, one building lists only the positions allowed there (plus any its shifts use); All buildings keeps them all.
  return list.length ? list : axisPositions(positions, locationId, shifts);
}
/** The section header's All / None. */
export const rotaFilterAll = (f: RotaFilters, k: RotaFilterSection): RotaFilters => ({ ...f, [k]: [], [NONE[k]]: false });
export const rotaFilterNone = (f: RotaFilters, k: RotaFilterSection): RotaFilters => ({ ...f, [k]: [], [NONE[k]]: true });
/**
 * Open shifts have nobody to be in a department, so a department filter keeps them (they're what the manager is trying
 * to fill); a position filter applies to them as to everyone. A section with every box cleared shows nothing.
 */
export function filterRota<S extends RotaShift>(shifts: readonly S[], f: RotaFilters, teamMembers: ReadonlyMap<string, ReadonlySet<string>>): S[] {
  if (f.nonePositions || f.noneTeams || f.noneGroups) return [];
  const groups = picked(f, 'groups');
  return shifts.filter(s => {
    if (f.positions.length && !f.positions.includes(s.position_id ?? '')) return false;
    if (f.teams.length && s.user_id && !f.teams.some(t => teamMembers.get(t)?.has(s.user_id!))) return false;
    if (groups.length && s.user_id && !groups.some(t => teamMembers.get(t)?.has(s.user_id!))) return false;
    return true;
  });
}

export type RotaGroupBy = 'position' | 'building';
export interface RotaGroup<S> { key: string; label: string; color: string | null; shifts: S[]; people: number; open: number }
/** The day's shifts in groups (position, or building when several are shown), by name; "No position" last. Inside: by start, then person before open. */
export function rotaGroups<S extends RotaShift>(shifts: readonly S[], date: string, by: RotaGroupBy, names: { position: (id: string) => { name: string; color: string | null } | undefined; building: (id: string) => string }): RotaGroup<S>[] {
  const by_ = new Map<string, S[]>();
  for (const s of shifts) if (live(s) && s.local_date === date) {
    const k = by === 'position' ? (s.position_id ?? '') : s.location_id;
    by_.set(k, [...(by_.get(k) ?? []), s]);
  }
  return [...by_.entries()].map(([key, list]) => {
    const p = by === 'position' && key ? names.position(key) : undefined;
    const label = by === 'position' ? (p?.name ?? 'No position') : names.building(key);
    return {
      key, label, color: p?.color ?? null,
      shifts: list.sort((a, b) => a.starts_at.localeCompare(b.starts_at) || Number(!a.user_id) - Number(!b.user_id) || a.id.localeCompare(b.id)),
      people: list.filter(s => s.user_id).length, open: list.filter(s => !s.user_id).reduce((n, s) => n + shiftPlaces(s), 0),
    };
  }).sort((a, b) => (a.key === '' ? 1 : 0) - (b.key === '' ? 1 : 0) || a.label.localeCompare(b.label));
}

// ------------------------------------------------------------------------------------------------ who's in
export type WhosInGroupId = 'no_show' | 'working' | 'on_break' | 'scheduled' | 'clocked_out' | 'on_leave';
export const WHOS_IN_GROUPS: { id: WhosInGroupId; label: string; tone: 'bad' | 'ok' | 'none' }[] = [
  { id: 'no_show', label: 'Not in', tone: 'bad' }, { id: 'working', label: 'In', tone: 'ok' }, { id: 'on_break', label: 'On break', tone: 'none' },
  { id: 'scheduled', label: 'Later', tone: 'none' }, { id: 'clocked_out', label: 'Finished', tone: 'none' }, { id: 'on_leave', label: 'Off', tone: 'none' },
];
/** Today's attendance by status, Not in first (it needs a call). People with no shift and no clock-in are left out. Late is a chip on the row. */
export function whosInGroups<R extends Pick<AttendanceRow, 'primary' | 'statuses' | 'display_name' | 'shifts'>>(rows: readonly R[]): { id: WhosInGroupId; label: string; tone: 'bad' | 'ok' | 'none'; rows: R[] }[] {
  return WHOS_IN_GROUPS.map(g => ({
    ...g, rows: rows.filter(r => (r.primary as LiveStatus) === g.id).sort((a, b) => (a.shifts[0]?.starts_at ?? '').localeCompare(b.shifts[0]?.starts_at ?? '') || a.display_name.localeCompare(b.display_name)),
  })).filter(g => g.rows.length);
}
export const arrivedLate = (r: Pick<AttendanceRow, 'statuses'>) => r.statuses.includes('late');
/**
 * t256u: the Who's in search. Keeps people whose name, position or department contains the text (case- and
 * accent-insensitive, en-GB); blank text keeps everyone. `departmentNames` gives a row's department names.
 */
export function filterWhosIn<R extends Pick<AttendanceRow, 'display_name'> & { position?: { name: string } | null }>(rows: readonly R[], query: string, departmentNames: (r: R) => readonly string[] = () => []): R[] {
  const q = foldGB(query.trim());
  if (!q) return [...rows];
  return rows.filter(r => [r.display_name, r.position?.name ?? '', ...departmentNames(r)].some(t => foldGB(t).includes(q)));
}
const foldGB = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('en-GB');

// ------------------------------------------------------------------------------------------------ shift sheet: warnings and people
/** Warnings in the shift sheet use the desktop's grouping (workforce/warningGroups `groupWarnings`): one line per type. Saving is blocked only by errors, as on desktop. */
export const warningsBlock = (ws: readonly Pick<WtrWarning, 'severity'>[]) => ws.some(w => w.severity === 'error');

/** One line under a person in the picker: "Available", the first hint, or "+1 more". */
export function candidateLine(c: ShiftCandidate | undefined): { tone: 'ok' | 'warn' | 'bad'; text: string } {
  if (!c) return { tone: 'ok', text: '' };
  if (c.status === 'ok' && !c.hints.length) return { tone: 'ok', text: 'Available' };
  const first = c.hints.find(h => h.severity === 'error') ?? c.hints.find(h => h.severity === 'warning') ?? c.hints[0];
  const more = c.hints.length > 1 ? ` · +${c.hints.length - 1}` : '';
  return { tone: c.status === 'blocked' ? 'bad' : c.status === 'warning' ? 'warn' : 'ok', text: `${first?.message ?? 'Available'}${more}` };
}
/** People to pick from: the shift's position first (chips: up to 8 recent / matching), then the rest by name; blocked people last. */
export function sortCandidates<P extends { user_id: string; display_name: string; position_ids: string[] }>(people: readonly P[], o: { positionId: string | null; candidates?: ReadonlyMap<string, ShiftCandidate>; query?: string }): P[] {
  const q = (o.query ?? '').trim().toLowerCase();
  const rank = (p: P) => (o.candidates?.get(p.user_id)?.status === 'blocked' ? 2 : 0) + (o.positionId && !p.position_ids.includes(o.positionId) ? 1 : 0);
  return people.filter(p => !q || p.display_name.toLowerCase().includes(q))
    .sort((a, b) => rank(a) - rank(b) || a.display_name.localeCompare(b.display_name));
}

// ------------------------------------------------------------------------------------------------ copy, repeat, copy day
/** "HH:mm" in London for a shift's start and end (what the shift API takes). */
export const localTime = (iso: string) => DateTime.fromISO(iso).setZone('Europe/London').toFormat('HH:mm');
/** Repeat a shift: the rest of its week (after its day), or the days picked; never its own day. */
export function repeatDates(date: string, o: { mode: 'rest_of_week' } | { mode: 'days'; days: string[] }): string[] {
  if (o.mode === 'rest_of_week') return rotaWeek(date).filter(x => x > date);
  return [...new Set(o.days)].filter(x => x !== date).sort();
}
/** Bulk copy ops for one shift onto dates, with or without the person (open shifts stay open). */
export const copyShiftOps = (s: Pick<Shift, 'id' | 'user_id'>, dates: readonly string[], withPerson = true): BulkShiftOp[] =>
  dates.map(date => ({ op: 'copy', id: s.id, date, user_id: withPerson ? s.user_id : null }));
/** Copy a whole day onto another day: every live shift of `from`, with or without people. */
export const copyDayOps = (shifts: readonly RotaShift[], from: string, to: string, withPeople: boolean): BulkShiftOp[] =>
  from === to ? [] : shifts.filter(s => live(s) && s.local_date === from).map(s => ({ op: 'copy', id: s.id, date: to, user_id: withPeople ? s.user_id : null }));

// ------------------------------------------------------------------------------------------------ publish
export type PublishNotify = 'changed' | 'all' | 'none';
export const PUBLISH_NOTIFY: { id: PublishNotify; label: string }[] = [
  { id: 'changed', label: 'People with changes' }, { id: 'all', label: 'Everyone on the week' }, { id: 'none', label: 'Nobody' },
];
/** The publish bar and sheet: unpublished shifts in the week, by day (only days that have some). */
export function publishSummary(shifts: readonly RotaShift[], date: string): { total: number; ids: string[]; days: { date: string; label: string; n: number }[] } {
  const week = rotaWeek(date);
  const un = shifts.filter(s => live(s) && week.includes(s.local_date) && isUnpublished(s));
  return {
    total: un.length, ids: un.map(s => s.id),
    days: week.map(x => ({ date: x, label: rotaDayLabel(x), n: un.filter(s => s.local_date === x).length })).filter(x => x.n),
  };
}

// ------------------------------------------------------------------------------------------------ t183u: person search
// t233u: the derived buildingPositions() (staff based there + presets) is replaced by the explicit, admin-managed
// mapping in workforce/positionBuildings (positionsAt / buildingsFor).

/** Person search: name or job title, case- and accent-insensitive; `limit` rows shown (the rest counted). */
const fold = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
export function searchPeople<P extends { display_name: string; job_title?: string | null }>(people: readonly P[], query: string, limit = 50): { shown: P[]; more: number } {
  const q = fold(query.trim());
  const hits = q ? people.filter(p => fold(p.display_name).includes(q) || (p.job_title ? fold(p.job_title).includes(q) : false)) : [...people];
  return { shown: hits.slice(0, limit), more: Math.max(0, hits.length - limit) };
}

// ------------------------------------------------------------------------------------------------ t200u: all buildings
/** The building picker's "All buildings" value (remembered like a building id). */
export const ALL_BUILDINGS = 'all';
/** Shifts whose person's name contains the text (case-insensitive; open shifts drop out while a name is typed). */
export function filterRotaPerson<S extends RotaShift>(shifts: readonly S[], q: string, nameOf: (userId: string) => string | undefined): S[] {
  const t = q.trim().toLocaleLowerCase('en-GB');
  if (!t) return [...shifts];
  return shifts.filter(s => Boolean(s.user_id) && (nameOf(s.user_id!) ?? '').toLocaleLowerCase('en-GB').includes(t));
}
/** The week's unpublished shifts per building (by name), for publishing several buildings at once. */
export function publishBreakdown(shifts: readonly RotaShift[], date: string, buildingName: (id: string) => string): { location_id: string; name: string; n: number; ids: string[] }[] {
  const week = rotaWeek(date);
  const by = new Map<string, string[]>();
  for (const s of shifts) if (live(s) && week.includes(s.local_date) && isUnpublished(s)) by.set(s.location_id, [...(by.get(s.location_id) ?? []), s.id]);
  return [...by.entries()].map(([location_id, ids]) => ({ location_id, name: buildingName(location_id), n: ids.length, ids })).sort((a, b) => a.name.localeCompare(b.name, 'en-GB'));
}
